/* eslint-disable @typescript-eslint/no-unused-vars, no-unreachable, no-loop-func */
import React from 'react';
import {
  Card,
  Input,
  Button,
  List,
  Typography,
  Alert,
  Row,
  Col,
  Tooltip,
  Tag,
  Modal,
  Spin,
  Progress,
} from 'antd';
import { API } from './services/api';

const { TextArea } = Input;
const { Text, Paragraph } = Typography;

const chunkText = (str: string, size: number) => {
  const chunks = [];
  let index = 0;
  while (index < str.length) {
    chunks.push(str.slice(index, index + size));
    index += size;
  }
  return chunks;
};

// 텍스트를 단어 기준으로 청크로 나누기
const chunkTextByWords = (text: string, wordsPerChunk: number = 250): string[] => {
  const words = text.split(/\s+/);
  const chunks: string[] = [];
  
  for (let i = 0; i < words.length; i += wordsPerChunk) {
    const chunk = words.slice(i, i + wordsPerChunk).join(' ');
    if (chunk.trim()) {
      chunks.push(chunk);
    }
  }
  
  return chunks.length > 0 ? chunks : [text];
};

const SpellCheck = () => {
  const [text, setText] = React.useState('');
  const [results, setResults] = React.useState<
    {
      info: string;
      suggestions: string[];
      token: string;
    }[]
  >([]);
  const [highlightedText, setHighlightedText] =
    React.useState<React.ReactNode>(null);
  const [modifiedText, setModifiedText] = React.useState('');
  const [loading, setLoading] = React.useState<undefined | boolean>();
  const [modalVisible, setModalVisible] = React.useState(false);
  const [progress, setProgress] = React.useState(0);
  const [completedChunks, setCompletedChunks] = React.useState(0);
  const [totalChunks, setTotalChunks] = React.useState(0);
  const abortControllerRef = React.useRef<AbortController | null>(null);

  // 컴포넌트 언마운트 시 요청 취소
  React.useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  // 배치로 요청을 처리하는 함수
  const processBatch = async (
    chunks: string[],
    batchSize: number = 5,
    maxRetries: number = 2
  ) => {
    const allResults: any[] = [];
    const chunkResults: { data: any[]; index: number }[] = new Array(chunks.length);

    // 청크를 배치로 나누기
    for (let i = 0; i < chunks.length; i += batchSize) {
      const batch = chunks.slice(i, i + batchSize);
      
      // 배치 내의 청크들을 병렬로 처리
      const batchPromises = batch.map(async (chunk, batchIndex) => {
        const chunkIndex = i + batchIndex;
        let retryCount = 0;
        
        while (retryCount <= maxRetries) {
          try {
            const response = await API.post('/spell-check', { text: chunk }, {
              signal: abortControllerRef.current?.signal,
            });
            
            const result = { data: response.data, index: chunkIndex };
            chunkResults[chunkIndex] = result;
            
            // 각 청크가 완료될 때마다 진행률 업데이트 및 결과 즉시 반영
            setCompletedChunks((prev) => {
              const newCount = prev + 1;
              const newProgress = Math.round((newCount / chunks.length) * 100);
              setProgress(newProgress);
              return newCount;
            });

            // 완료된 청크 결과를 즉시 반영하여 점진적으로 표시
            // 성능 최적화를 위해 배치 단위로만 업데이트
            return result;
            
            return result;
          } catch (error: any) {
            if (error.name === 'CanceledError' || error.code === 'ERR_CANCELED') {
              throw error; // 취소된 요청은 재시도하지 않음
            }
            
            retryCount++;
            if (retryCount > maxRetries) {
              console.error(`청크 ${chunkIndex + 1} 처리 실패 (재시도 ${maxRetries}회 실패):`, error);
              chunkResults[chunkIndex] = { data: [], index: chunkIndex };
              setCompletedChunks((prev) => {
                const newCount = prev + 1;
                const newProgress = Math.round((newCount / chunks.length) * 100);
                setProgress(newProgress);
                return newCount;
              });
              return { data: [], index: chunkIndex };
            }
            
            // 재시도 전 대기 (지수 백오프)
            await new Promise((resolve) => setTimeout(resolve, 1000 * retryCount));
          }
        }
      });

      // 배치가 완료될 때까지 대기
      await Promise.allSettled(batchPromises);
      
      // 배치 완료 후 중간 결과 업데이트 (점진적 표시)
      const currentResults = chunkResults
        .filter((r) => r !== undefined)
        .sort((a, b) => a.index - b.index)
        .flatMap((r) => r.data);
      
      setResults([...currentResults]);
      highlightErrors([...currentResults]);
    }

    // 최종 결과 수집 및 정렬
    return chunkResults
      .filter((r) => r !== undefined)
      .sort((a, b) => a.index - b.index)
      .flatMap((r) => r.data);
  };

  const onCheck = async () => {
    // 이전 요청 취소
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    
    // 새로운 AbortController 생성
    abortControllerRef.current = new AbortController();
    
    setResults([]); // Reset results
    setModifiedText(''); // Reset modified text
    setLoading(true);
    setProgress(0);
    setCompletedChunks(0);

    try {
      // 텍스트를 더 작은 청크로 나누기 (100단어씩으로 분할하여 요청 수 증가)
      // 작은 청크는 각 요청이 더 빠르게 처리될 수 있음
      const chunks = chunkTextByWords(text, 100);
      setTotalChunks(chunks.length);

      if (chunks.length === 0) {
        setLoading(false);
        return;
      }

      // 배치 처리로 요청 (동시에 3개씩 처리하여 서버 부하 분산)
      const allResults = await processBatch(chunks, 3, 2);

      setResults(allResults);
      highlightErrors(allResults);
    } catch (error: any) {
      if (error.name === 'CanceledError' || error.code === 'ERR_CANCELED') {
        console.log('요청이 취소되었습니다.');
        return;
      }
      console.log(error);
      alert('오류가 발생했습니다.');
    } finally {
      setLoading(false);
      setTimeout(() => {
        setProgress(0);
        setCompletedChunks(0);
        setTotalChunks(0);
      }, 500);
    }
  };

  const highlightErrors = (errors: any[]) => {
    const elements: React.ReactNode[] = [];

    const escapeRegExp = (string: string) => {
      return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    };

    const lines = text.split(/\n/); // Split the text by line breaks
    lines.forEach((line, lineIndex) => {
      let updatedLine = line;

      errors.forEach((error) => {
        const escapedToken = escapeRegExp(error.token);
        const regex = new RegExp(
          `(?<!<span class="error-word".*?>)(${escapedToken})(?!</span>)(?!\\w)`,
          'g'
        );
        updatedLine = updatedLine.replace(
          regex,
          `<span class="error-word" style="color: red; font-weight: bold;">$1</span>`
        );
      });

      // Split by spans to maintain structure
      const lineParts = updatedLine.split(
        /(<span class="error-word".*?<\/span>)/g
      );
      lineParts.forEach((part, index) => {
        if (part.startsWith('<span')) {
          const tokenMatch = part.match(/>(.*?)<\/span>/);
          const token = tokenMatch ? tokenMatch[1] : '';

          const error = errors.find((e) => e.token === token.trim());

          // Get the first suggestion if available
          const firstSuggestion = error?.suggestions[0];

          // Create tooltip content
          const tooltipContent = (
            <div style={{ whiteSpace: 'pre-wrap' }}>
              {error?.info && <Alert message={error.info} type="warning" />}
              <br />
              {error?.suggestions.length > 0 && (
                <>
                  <b>
                    <Text>추천 수정 사항</Text>
                    <br />
                    {error.suggestions.map((s: any) => (
                      <Tag key={s}>{s}</Tag>
                    ))}
                  </b>
                </>
              )}
            </div>
          );

          // Push the suggestion above the error text if available
          elements.push(
            <div
              key={`error-container-${lineIndex}-${index}`}
              style={{
                position: 'relative',
                display: 'inline-block',
              }}
            >
              {firstSuggestion && (
                <div
                  style={{
                    position: 'absolute',
                    bottom: '100%', // Position above the error text
                    left: '50%',
                    transform: 'translateX(-50%)',
                    color: 'orange',
                    marginBottom: '-11px', // Space between suggestion and error
                    whiteSpace: 'nowrap', // Prevent line breaks
                    fontSize: 12,
                  }}
                >
                  {firstSuggestion}
                </div>
              )}
              <Tooltip
                style={{ whiteSpace: 'pre-wrap' }}
                title={tooltipContent}
                key={`${lineIndex}-${index}`}
                color="white"
              >
                <span dangerouslySetInnerHTML={{ __html: part }} />
              </Tooltip>
            </div>
          );
        } else {
          elements.push(part); // Add non-error parts as they are
        }
      });

      // Add a line break after each processed line
      elements.push(<br key={`br-${lineIndex}`} />);
    });

    // Set the highlighted text with increased line height
    setHighlightedText(
      <div style={{ lineHeight: '1.9' }}>{elements}</div> // Adjust the line height as needed
    );
  };

  const applySuggestions = () => {
    let updatedText = text;

    results.forEach((result) => {
      if (result.suggestions.length > 0) {
        // 첫 번째 suggestion을 적용
        const suggestion = result.suggestions[0];
        // 원본 텍스트에서 잘못된 token을 suggestion으로 대체
        const regex = new RegExp(result.token, 'g'); // 전체 문장에서 모든 일치하는 부분을 찾기 위해 g 플래그 사용
        updatedText = updatedText.replace(regex, suggestion);
      }
    });

    setModifiedText(updatedText); // 수정된 텍스트 상태에 반영
    setModalVisible(true); // 수정된 텍스트 모달로 표시
  };

  return (
    <Card
      title="맞춤법 검사기"
      bordered={false}
      style={{ maxWidth: 1200, margin: '0 auto' }}
    >
      <Row gutter={24}>
        {/* 왼쪽 텍스트 입력 영역 */}
        <Col span={12}>
          <Typography.Text strong>글자수: {text.length}</Typography.Text>
          <TextArea
            showCount
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="여기에 텍스트를 입력하세요"
            style={{ marginBottom: 20 }}
            autoSize={{ minRows: 10 }}
          />

          <Button
            type="primary"
            onClick={onCheck}
            loading={loading}
            style={{ marginBottom: 20 }}
            block
          >
            맞춤법 체크
          </Button>

          {results.length === 0 &&
            !loading &&
            loading !== undefined &&
            text && (
              <Alert
                message="모두 올바른 문장입니다."
                type="success"
                style={{ marginTop: 20 }}
              />
            )}

          <Modal
            open={modalVisible}
            onCancel={() => {
              setModalVisible(false);
            }}
            footer={null}
          >
            <Card
              title={
                <div>
                  <Text strong>수정사항 적용 결과</Text>
                </div>
              }
            >
              <TextArea
                showCount
                value={modifiedText}
                onChange={(e) => setModifiedText(e.target.value)}
                placeholder="여기에 텍스트를 입력하세요"
                style={{ marginBottom: 20 }}
                autoSize={{ minRows: 10 }}
              />
            </Card>
          </Modal>

          {results.length > 0 && (
            <List
              header={
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                >
                  <Text strong>검사 결과</Text>
                </div>
              }
              bordered
              dataSource={results}
              renderItem={(result) => (
                <List.Item>
                  <div>
                    {result.info && (
                      <Alert
                        message={result.info}
                        type="warning"
                        style={{
                          fontSize: 11,
                        }}
                      />
                    )}
                    <Text
                      strong
                      style={{ display: 'block', marginTop: 10, fontSize: 12 }}
                    >
                      문제 있는 단어: {result.token}
                    </Text>
                    {result.suggestions.length > 0 && (
                      <Text
                        type="secondary"
                        style={{ marginTop: 5, display: 'block', fontSize: 12 }}
                      >
                        추천 수정 사항: {result.suggestions.join(', ')}
                      </Text>
                    )}
                  </div>
                </List.Item>
              )}
            />
          )}
        </Col>

        {/* 오른쪽 검사 결과 영역 */}
        <Col span={12}>
          {loading && (
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: 300,
                flexDirection: 'column',
                gap: 8,
              }}
            >
              <Spin tip="  맞춤법 검사중.." size="large" />
              <Typography
                style={{
                  fontSize: 16,
                  color: '#1677ff',
                }}
              >
                맞춤법 검사중...
              </Typography>
              <div style={{ width: '100%', marginTop: 20 }}>
                <Progress
                  percent={progress}
                  status="active"
                  format={(percent) => `${percent}%`}
                />
                {totalChunks > 0 && (
                  <Typography.Text type="secondary" style={{ fontSize: 12, display: 'block', textAlign: 'center', marginTop: 8 }}>
                    {completedChunks} / {totalChunks} 청크 처리 중...
                  </Typography.Text>
                )}
              </div>
            </div>
          )}

          {results.length > 0 && (
            <Card
              title={
                <div>
                  <Text strong>검사 결과 적용 텍스트</Text>

                  <Button
                    type="primary"
                    onClick={applySuggestions}
                    style={{ float: 'right' }}
                  >
                    맞춤법 검사 모두 적용
                  </Button>
                </div>
              }
            >
              <div style={{}}>{highlightedText}</div>
            </Card>
          )}
        </Col>
      </Row>
    </Card>
  );
};

export default SpellCheck;